import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  LayoutDashboard,
  FileText,
  Users,
  LogOut,
  Plus,
  Trash2,
  Shield,
  Menu,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const roleLabels = {
  admin: { label: "Administrador", color: "bg-purple-100 text-purple-800" },
  gestor: { label: "Gestor", color: "bg-blue-100 text-blue-800" },
  investigador: { label: "Investigador", color: "bg-green-100 text-green-800" },
  leitor: { label: "Leitor", color: "bg-slate-100 text-slate-800" },
};

export default function UsersManagement() {
  const { user, logout, register } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    nome: "",
    email: "",
    senha: "",
    role: "leitor",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await register(newUser.nome, newUser.email, newUser.senha, newUser.role);
      toast.success("Usuário criado com sucesso!");
      setDialogOpen(false);
      setNewUser({ nome: "", email: "", senha: "", role: "leitor" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar usuário");
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/users/${userId}/role?role=${newRole}`);
      toast.success("Perfil atualizado!");
      fetchUsers();
    } catch (error) {
      toast.error("Erro ao atualizar perfil");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/users/${userId}`);
      toast.success("Usuário removido!");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao remover usuário");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("pt-BR");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <Link to="/admin" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-semibold text-[#1a1a2e]">CONTERP</h1>
                  <p className="text-xs text-slate-500">Compliance</p>
                </div>
              </Link>
              <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            <Link to="/admin" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </Link>
            <Link to="/admin/casos" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
              <FileText className="w-5 h-5" />
              Casos
            </Link>
            <Link to="/admin/usuarios" className="flex items-center gap-3 px-4 py-3 text-[#1a1a2e] bg-slate-100 rounded-lg font-medium">
              <Users className="w-5 h-5" />
              Usuários
            </Link>
          </nav>

          <div className="p-4 border-t border-slate-200">
            <Button variant="outline" className="w-full justify-start" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-semibold text-[#1a1a2e]">Gestão de Usuários</h1>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#1a1a2e] hover:bg-[#1a1a2e]/90" data-testid="btn-new-user">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Usuário
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Criar Novo Usuário</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <Label>Nome</Label>
                    <Input
                      value={newUser.nome}
                      onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                      className="mt-2"
                      data-testid="new-user-nome"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="mt-2"
                      data-testid="new-user-email"
                    />
                  </div>
                  <div>
                    <Label>Senha</Label>
                    <Input
                      type="password"
                      value={newUser.senha}
                      onChange={(e) => setNewUser({ ...newUser, senha: e.target.value })}
                      className="mt-2"
                      data-testid="new-user-senha"
                    />
                  </div>
                  <div>
                    <Label>Perfil</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger className="mt-2" data-testid="new-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrador</SelectItem>
                        <SelectItem value="gestor">Gestor</SelectItem>
                        <SelectItem value="investigador">Investigador</SelectItem>
                        <SelectItem value="leitor">Leitor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full bg-[#1a1a2e]" data-testid="btn-create-user">
                    Criar Usuário
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <div className="flex-1 p-6">
          {/* RBAC Info */}
          <div className="bg-[#1a1a2e]/5 rounded-xl p-4 mb-6 flex items-start gap-3">
            <Shield className="w-5 h-5 text-[#1a1a2e] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-[#1a1a2e] mb-1">Controle de Acesso (RBAC)</p>
              <ul className="text-slate-600 space-y-1">
                <li><strong>Administrador:</strong> Acesso total ao sistema</li>
                <li><strong>Gestor:</strong> Gerencia casos, atribui responsáveis, define prioridades</li>
                <li><strong>Investigador:</strong> Trabalha em casos atribuídos, cria despachos e tarefas</li>
                <li><strong>Leitor:</strong> Visualiza casos sem poder editar</li>
              </ul>
            </div>
          </div>

          {/* Users List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Usuário</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Email</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Perfil</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Criado em</th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#1a1a2e] rounded-full flex items-center justify-center text-white font-medium">
                            {u.nome?.[0]?.toUpperCase()}
                          </div>
                          <span className="font-medium text-[#1a1a2e]">{u.nome}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{u.email}</td>
                      <td className="px-6 py-4">
                        <Select
                          value={u.role}
                          onValueChange={(v) => handleUpdateRole(u.id, v)}
                          disabled={u.id === user?.id}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Administrador</SelectItem>
                            <SelectItem value="gestor">Gestor</SelectItem>
                            <SelectItem value="investigador">Investigador</SelectItem>
                            <SelectItem value="leitor">Leitor</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{formatDate(u.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        {u.id !== user?.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remover Usuário?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta ação não pode ser desfeita. O usuário {u.nome} será removido permanentemente.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Remover
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
